import * as React from 'react';
import { Alert as MatAlert, AlertColor, SxProps, Theme } from '@mui/material';
import { Icon } from '../Icon';
import { archerConstants } from '../../utils/themeProvider';
import { createButton, ButtonBlueprint } from '../../factories/button';

const styles: Record<string, SxProps<Theme>> = {
  AlertRoot: {
    margin: '1rem',
    padding: '1rem',
    borderWidth: '2px',
    '&[class*="MuiAlert-outlinedError"]': { borderColor: '#C62828', '& [class*="MuiAlert-icon"]': { color: '#C62828' } },
    '&[class*="MuiAlert-outlinedInfo"]': { borderColor: '#1565C0', '& [class*="MuiAlert-icon"]': { color: '#1565C0' } },
    '&[class*="MuiAlert-outlinedSuccess"]': { borderColor: '#3E9342', '& [class*="MuiAlert-icon"]': { color: '#3E9342' } },
    '&[class*="MuiAlert-outlinedWarning"]': { borderColor: '#CC6600', '& [class*="MuiAlert-icon"]': { color: '#CC6600' } },
    '& [class*="MuiAlert-icon"]': {
      padding: '0 1rem 0 0',
      margin: 0
    },
    '& [class*="MuiAlert-message"]': {
      padding: '0',
      '& [class*="MuiTypography-root"]': {
        color: archerConstants.archerTextPrimary,
        '& [class*="MuiSvgIcon-root"]': {
          position: 'relative',
          marginTop: '-4px',
          top: '4px',
          padding: '0 4px'
        }
      }
    },
    '& [class*="MuiAlert-action"]': {
      alignItems: 'baseline',
      marginTop: '-5px',
      marginBottom: '-5px',
      '& button': {
        padding: '0 0.5rem',
        margin: 0,
        maxHeight: '1.875rem',
        color: 'primary.main',
        '&:hover': {
          color: archerConstants.archerContrastRatioSafe
        }
      }
    }
  }
};

export interface AlertProps {
  actionList?: ButtonBlueprint[];
  severity: AlertColor;
  className?: string;
  content: React.ReactNode;
  dataProp?: string;
  sx?: SxProps<Theme>;
}

const Alert: React.FunctionComponent<AlertProps> = ({ actionList, severity, className, content, dataProp, sx }: AlertProps) => {
  const iconMapping = {
    success: <Icon icon={'CheckCircle'} fontSize="medium" />,
    error: <Icon icon={'Error'} fontSize="medium" />,
    info: <Icon icon={'Info'} fontSize="medium" />,
    warning: <Icon icon={'Warning'} fontSize="medium" />
  };

  const actionSection = actionList
    ? actionList.map((action: ButtonBlueprint) =>
        createButton({
          ...action,
          actionType: 'inherit',
          size: 'small'
        })
      )
    : undefined;

  return (
    <MatAlert
      sx={[styles.AlertRoot, sx] as SxProps<Theme>}
      action={actionSection}
      severity={severity}
      iconMapping={iconMapping}
      variant="outlined"
      className={className}
      data-selector={dataProp}
    >
      {content}
    </MatAlert>
  );
};
Alert.displayName = 'Alert';

export { Alert };
